<template>
	<div
		class="ProgrammeHeader"
		:class="{ withLogo: hasVisibleLogo }"
	>
		<ProgrammeExternalServices
			:programmeId="programmeId"
			:isMasters="isMasters"
			:loading="externalServicesLoading"
		/>

		<OrganisationLogo
			v-if="hasVisibleLogo"
			class="Logo"
			:imageUrl="optimizedOrganisationUrl"
			:organisationName="organisationName"
			:loading="loading"
		/>

		<div class="Info">
			<div class="OrganisationInfo">
				<strong
					v-skeleton="{
						loading,
						sizes: {
							Small: { width: '7.5625rem', height: '0.75rem' },
						}
					}"
					class="OrganisationName"
					:title="organisationName"
				>
					{{ organisationName }}
				</strong>
				<RatingCount
					:reviewRating="reviewRating"
					:loading="loading"
				/>
			</div>
			<span
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '11.563rem', height: '0.75rem' },
					},
					margins: {
						Small: '0.25rem 0',
					},
				}"
				class="Locations"
			>
				{{ mainLocation }} <template v-if="extraLocationsCount">+{{ extraLocationsCount }}</template>
			</span>
			<span
				v-if="ranking || loading"
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '6.75rem', height: '0.75rem' },
					}
				}"
				class="Ranking"
			>
				Top {{ ranking }}% in Worldwide
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import {PortalType} from '@studyportals/domain-client';
import OrganisationLogo from '@components/SearchResults/Card/OrganisationLogo/OrganisationLogo.vue';
import RatingCount from '@components/RatingCount/RatingCount.vue';
import ProgrammeExternalServices from '../ProgrammeExternalServices/ProgrammeExternalServices.vue';
import { useSearchStore } from '@stores/useSearchStore';
import { IReviewRating } from '@app/interfaces/dtos/IReviewRating';

const props = defineProps({
	organisationName: {
		type: String,
		default: '',
	},
	organisationLogoUrl: {
		type: String,
		default: '',
	},
	programmeId: {
		type: String,
		default: '',
	},
	reviewRating: {
		type: Object as PropType<IReviewRating | undefined>,
		default: undefined,
	},
	ranking: {
		type: String,
		required: false,
	},
	showLogo: {
		type: Boolean,
		default: true,
	},
	locations: {
		type: Array as () => string[],
		required: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	externalServicesLoading: {
		type: Boolean,
		default: false,
	},
});

const optimizedOrganisationUrl = computed(() => {
	if (!props.organisationLogoUrl) {
		return '';
	}

	return `${props.organisationLogoUrl}?auto=format&fit=fill&fill=solid&fill-color=00FFFFFF&w=40&h=40&q=70`;
});

const searchStore = useSearchStore();
const isMasters = computed(() => searchStore.portalType === PortalType.MASTER);
const hasVisibleLogo = computed(() => props.showLogo || props.loading);
const mainLocation = computed(() => props.locations && props.locations.length > 0 ? props.locations[0] : '');
const extraLocationsCount = computed(() => props.locations && props.locations.length > 1 ? props.locations.length - 1 : 0);
</script>

<style scoped lang="scss">
	.ProgrammeHeader {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		grid-template-areas:
			'external'
			'info';
		align-items: start;
		width: 100%;
		overflow: hidden;
		gap: 0.25rem;

		&.withLogo {
			grid-template-columns: auto minmax(0, 1fr);
			grid-template-areas:
				'external external'
				'logo info';
			column-gap: 0.5rem;
		}

		@include Breakpoint(Large, ExtraLarge) {
			grid-template-columns: minmax(0, 1fr) auto;
			grid-template-areas: 'info external';

			&.withLogo {
				grid-template-columns: auto minmax(0, 1fr) auto;
				grid-template-areas: 'logo info external';
			}
		}

		.Logo {
			grid-area: logo;
		}

		.Info {
			grid-area: info;
			display: flex;
			justify-content: start;
			align-items: start;
			flex-direction: column;
			max-width: 100%;
			overflow: hidden;

			@include MarginAreas(0);

			.OrganisationInfo {
				display: flex;
				gap: 0.25rem;
				width: 100%;
				@include MarginAreas(0);

				.OrganisationName {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					color: $Grey-800;

					@include TextStyle(UiSmallSemibold);
					@include MarginAreas(0);
					@include PaddingAreas(0, 0, 0.25, 0);
				}
			}

			.Locations {
				width: 100%;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				color: $Grey-700;

				@include TextStyle(UiSmallRegular);
				@include PaddingAreas(0.25, 0);
			}

			.Ranking {
				width: 100%;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				color: $Grey-800;

				@include TextStyle(UiSmallSemibold);
				@include PaddingAreas(0.25, 0);
			}
		}
	}
</style>
